import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { settingsSectionPath } from '@/routes/route-config';
import { useTranslation } from 'react-i18next';
import { useSubscription } from '@/hooks/useSubscription';
import { Button, Modal } from '@/components/ds';
import { Clock, CreditCard } from 'lucide-react';

const STORAGE_KEY = 'grace_period_reminder_dismissed_at';

function wasDismissedToday(): boolean {
  const dismissed = localStorage.getItem(STORAGE_KEY);
  if (!dismissed) return false;
  const today = new Date().toDateString();
  return new Date(dismissed).toDateString() === today;
}

export function GracePeriodReminder() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isInGracePeriod, daysUntilGraceEnds, subscriptionStatus, isLoading } =
    useSubscription();
  // Read once: whether today's reminder was already dismissed. The dialog is
  // open exactly while the clinic is in its grace period and it was not.
  const [dismissed, setDismissed] = useState(wasDismissedToday);
  const open = isInGracePeriod && !isLoading && !dismissed;

  const dismiss = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    setDismissed(true);
  }, []);

  return (
    <Modal
      isOpen={open}
      onClose={dismiss}
      title={t('subscription.gracePeriod.modalTitle')}
      icon={<Clock className="h-5 w-5 text-danger-500" />}
      maxWidth="sm"
      footer={
        <>
          <Button variant="secondary" size="sm" onPress={dismiss}>
            {t('subscription.gracePeriod.remindTomorrow')}
          </Button>
          <Button
            size="sm"
            onPress={() => {
              dismiss();
              navigate(settingsSectionPath('subscription'));
            }}
          >
            <CreditCard className="me-1.5 size-4" aria-hidden />
            {t('subscription.gracePeriod.renewNow')}
          </Button>
        </>
      }
    >
      <div className="text-center py-2">
        <div className="mx-auto w-14 h-14 bg-danger-100 rounded-full flex items-center justify-center mb-4">
          <span className="text-2xl font-bold text-danger-600">
            {daysUntilGraceEnds}
          </span>
        </div>
        <p
          className="text-neutral-700 text-sm"
          dangerouslySetInnerHTML={{
            __html: t('subscription.gracePeriod.daysLeft', {
              days: daysUntilGraceEnds,
              plural: daysUntilGraceEnds !== 1 ? 's' : '',
            }),
          }}
        />
        {subscriptionStatus?.endDate && (
          <p className="text-xs text-neutral-500 mt-2">
            {t('subscription.gracePeriod.expiredOn', {
              date: new Date(subscriptionStatus.endDate).toLocaleDateString(),
            })}
          </p>
        )}
      </div>
    </Modal>
  );
}
